import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, BookOpen, CheckCircle2, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { SEO, generateArticleSchema } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { FloatingShapes } from "@/components/ui/GlowingOrb";
import { TextReveal } from "@/components/ui/TextReveal";
import { ScrollProgress } from "@/components/ui/ScrollProgress";
import type { InsightPage } from "@/data/contentClusters";

interface InsightsPageTemplateProps {
  page: InsightPage;
  relatedMoneyPages: { title: string; slug: string; pillarSlug: string }[];
  relatedInsightPages: { title: string; slug: string }[];
}

export function InsightsPageTemplate({
  page,
  relatedMoneyPages,
  relatedInsightPages,
}: InsightsPageTemplateProps) {
  const primaryPillar = page.pillarSlugs[0];

  // Generate article schema for AEO/GEO
  const articleSchema = generateArticleSchema({
    headline: page.h1,
    description: page.metaDescription,
    url: `https://thecloudseo.com/insights/${page.slug}`,
  });

  return (
    <Layout>
      <SEO
        title={`${page.title} | The Cloud SEO`}
        description={page.metaDescription}
        canonical={`https://thecloudseo.com/insights/${page.slug}`}
        type="article"
        jsonLd={articleSchema}
        articleSection={page.category}
        readingTime={page.readingTime}
        keywords={[page.h1, page.category, 'AI marketing', 'insights']}
        speakable={['h1', '.key-takeaways']}
      />
      <ScrollProgress />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <AnimatedBackground variant="mesh" />

        <div className="container-narrow relative z-10">
          {/* Breadcrumbs */}
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Insights", href: "/insights" },
              { label: page.h1 },
            ]}
            className="mb-6"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-6"
          >
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              All Insights
            </Link>
            <span className="text-muted-foreground">•</span>
            <span className="inline-flex items-center gap-1.5 text-sm">
              <Tag className="w-4 h-4" />
              {page.category}
            </span>
          </motion.div>

          <TextReveal>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              {page.h1}
            </h1>
          </TextReveal>

          <TextReveal delay={0.1}>
            <p className="text-xl text-muted-foreground mb-8">{page.subtitle}</p>
          </TextReveal>

          <motion.div
            className="flex items-center gap-6 text-sm text-muted-foreground"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {page.readingTime} read
            </span>
            <span className="flex items-center gap-1.5">
              <BookOpen className="w-4 h-4" />
              {page.sections.length} sections
            </span>
          </motion.div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-8 border-y border-border bg-secondary/20">
        <div className="container-narrow">
          <div className="flex flex-wrap gap-4">
            <span className="text-sm font-semibold text-muted-foreground">
              In this article:
            </span>
            {page.tableOfContents.map((item, index) => (
              <a
                key={index}
                href={`#${page.sections[index]?.id || ""}`}
                className="text-sm text-primary hover:underline"
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="section-spacing">
        <div className="container-narrow">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            {page.sections.map((section, index) => (
              <AnimatedSection key={section.id} delay={index * 0.05}>
                <section id={section.id} className="mb-12 scroll-mt-24">
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">{section.title}</h2>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {section.content}
                  </p>
                </section>
              </AnimatedSection>
            ))}
          </div>

          {/* Key Takeaways */}
          <AnimatedSection>
            <div className="mt-16 p-8 rounded-3xl bg-primary/5 border border-primary/20">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-primary" />
                Key Takeaways
              </h3>
              <ul className="space-y-4">
                {page.keyTakeaways.map((takeaway, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-lg">{takeaway}</span>
                  </li>
                ))}
              </ul>
            </div>
          </AnimatedSection>
        </div>
      </article>

      {/* Related Services */}
      {relatedMoneyPages.length > 0 && (
        <section className="section-spacing bg-secondary/30">
          <div className="container-wide">
            <AnimatedSection className="text-center mb-12">
              <TextReveal>
                <h2 className="heading-section mb-4">Related Services</h2>
              </TextReveal>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Put these insights into action with our services
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedMoneyPages.slice(0, 3).map((service, index) => (
                <AnimatedSection key={service.slug} delay={index * 0.1}>
                  <Link
                    to={`/services/${service.pillarSlug}/${service.slug}`}
                    className="group block p-6 rounded-2xl bg-background border border-border hover:border-primary/50 transition-colors"
                  >
                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <span className="inline-flex items-center text-sm text-primary">
                      Learn more{" "}
                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Insights */}
      {relatedInsightPages.length > 0 && (
        <section className="section-spacing">
          <div className="container-wide">
            <AnimatedSection className="text-center mb-12">
              <TextReveal>
                <h2 className="heading-section mb-4">Continue Reading</h2>
              </TextReveal>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedInsightPages
                .filter((i) => i.slug !== page.slug)
                .slice(0, 3)
                .map((insight, index) => (
                  <AnimatedSection key={insight.slug} delay={index * 0.1}>
                    <Link
                      to={`/insights/${insight.slug}`}
                      className="group block p-6 rounded-2xl bg-secondary/30 border border-border hover:border-primary/50 transition-colors"
                    >
                      <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                        {insight.title}
                      </h3>
                      <span className="inline-flex items-center text-sm text-primary">
                        Read article{" "}
                        <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Link>
                  </AnimatedSection>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-spacing bg-primary text-primary-foreground relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ["0% 0%", "100% 100%"],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="container-narrow text-center relative z-10">
          <AnimatedSection>
            <TextReveal>
              <h2 className="heading-section mb-6">{page.cta.headline}</h2>
            </TextReveal>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-10">
              {page.cta.description}
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                variant="secondary"
                className="rounded-full px-10 text-base h-14"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
